import { Draggable } from "@hello-pangea/dnd";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Button, ButtonBase, Checkbox, Collapse, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { get, sortBy } from "lodash";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Doc } from "../../../common/js/icons";
import { noPhotoUrl } from "../utils/constants";
import schema from "./AuditSearchTables";
import { SCOPES } from "./constants";
import PermissionsGate from "./permissions/PermissionsGate";

const StyledTableRow = styled("div")({
    alignItems: "center",
    fontSize: "13px",
    display: "flex",
    gap: "1vw",
});

const StyledListingLink = styled("span")({
    color: "#2C99D2",
    fontWeight: 600,
    textDecoration: "underline",
    textDecorationColor: "blue",
    "&:hover": {
        cursor: "pointer",
        textDecorationColor: "#2C99D2",
    },
});

const StyledDocumentButton = styled(Button)({
    fontWeight: "400",
    minWidth: "0px",
    margin: "0px",
    marginRight: "5px",
    padding: "5px 10px",
});

const StyledDocumentLink = styled("span")(({ document }) => {
    const mediaAssetKey = get(document, "mediaAssetKey");

    return {
        cursor: mediaAssetKey ? "pointer" : "default",
        color: mediaAssetKey ? "#2C99D2" : "grey",
        fontWeight: 600,
        textDecoration: "none",
    };
});

const StyledDocumentButtonBase = styled(ButtonBase)({
    color: "#2C99D2",
    cursor: "pointer",
    fontWeight: 600,
    textDecoration: "none",
});

const StyledLink = styled(Link)({
    textDecoration: "none",
});

const StyledTooltipSpan = styled("span")({
    "&>a:hover": {
        cursor: "default",
    },
});

const AuditListRow = (props) => {
    const { comp, i, role, toggleAuditorListAuditFlag, viewDocument } = props;
    const {
        activeTab,
        auditFlag,
        batchDate,
        brokerAgents,
        dealKey,
        listingName,
        listingPhotoUrl,
        primaryAddress,
        propertyPhotoUrl,
        propertyType,
        transactionType,
    } = comp;

    const [expanded, setExpanded] = useState(false);
    const [sortedDocuments, setSortedDocuments] = useState([]);

    useEffect(() => {
        const { attachedRequiredDocuments, missingRequiredDocuments } = comp.attachedRequiredDocuments;
        loadDocuments(attachedRequiredDocuments, missingRequiredDocuments);
    }, []);

    const loadDocuments = (attached, missing) => {
        let aggregatedDocs = undefined;
        if (attached && missing) {
            aggregatedDocs = [...attached, ...missing];
        } else if (attached) {
            aggregatedDocs = attached;
        } else if (missing) {
            aggregatedDocs = missing;
        }
        setSortedDocuments(sortBy(aggregatedDocs), ["documentTypeName", "documentTypeKey"]);
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        // change background colour if dragging
        background: isDragging ? "#C5C5C5" : "",

        // styles we need to apply on draggables
        ...draggableStyle,
    });

    const getTableCellFlex = (header) => {
        if (header === "Address") {
            return "2";
        } else if (header === "Documents") {
            return "5";
        } else {
            return "1";
        }
    };

    const getTableFixedWidth = (header) => {
        if (header === "Address") {
            return "200px";
        } else {
            return "100px";
        }
    };

    return (
        <Draggable draggableId={`AuditorList Draggable - ${String(i)}`} index={i}>
            {(provided, snapshot) => {
                return (
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                    >
                        <StyledTableRow container key={`tr-${i}`} selectable="false">
                            {schema.viewHeaders(activeTab)["all_comps"].map((header, k) => {
                                const { name, prop, transform } = header;
                                switch (name) {
                                    case "ToggleToList":
                                        return (
                                            <PermissionsGate hide scopes={[SCOPES.canAddToList]}>
                                                <div
                                                    style={{
                                                        paddingLeft: "25px",
                                                        flex: getTableCellFlex(header.name),
                                                        minWidth: getTableFixedWidth(header.name),
                                                        maxWidth: getTableFixedWidth(header.name),
                                                    }}
                                                >
                                                    <Checkbox
                                                        data-testid={`${listingName}-checkbox`}
                                                        checked={!auditFlag}
                                                        color="primary"
                                                        onChange={() => {
                                                            toggleAuditorListAuditFlag(dealKey);
                                                        }}
                                                    />
                                                    <IconButton
                                                        aria-label="expand row"
                                                        size="small"
                                                        onClick={() => setExpanded(!expanded)}
                                                    >
                                                        {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                    </IconButton>
                                                </div>
                                            </PermissionsGate>
                                        );

                                    case "Property":
                                        return (
                                            <div
                                                key={`trc-${k}`}
                                                style={{
                                                    marginLeft: "-50px",
                                                    flex: getTableCellFlex(header.name),
                                                    minWidth: getTableFixedWidth(header.name),
                                                    maxWidth: getTableFixedWidth(header.name),
                                                }}
                                            >
                                                <div
                                                    className="propertySmallImageContainer"
                                                    style={{
                                                        width: "85px",
                                                        maxWidth: "85px",
                                                        height: "80px",
                                                        margin: "10px 30px",
                                                        backgroundImage: `url(${
                                                            listingPhotoUrl
                                                                ? listingPhotoUrl
                                                                : propertyPhotoUrl
                                                                  ? propertyPhotoUrl
                                                                  : noPhotoUrl
                                                        })`,
                                                    }}
                                                ></div>
                                            </div>
                                        );
                                    case "Address":
                                        return (
                                            <div
                                                key={`trc-${k}`}
                                                style={{
                                                    marginLeft: "24px",
                                                    fontSize: "13px",
                                                    flex: getTableCellFlex(header.name),
                                                    minWidth: getTableFixedWidth(header.name),
                                                    maxWidth: getTableFixedWidth(header.name),
                                                }}
                                            >
                                                <StyledLink
                                                    data-testid={`${listingName}-hyperlink`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    to={`/deal/${dealKey}`}
                                                >
                                                    <StyledListingLink data-testid="auditorListLink">
                                                        {listingName}
                                                    </StyledListingLink>
                                                </StyledLink>
                                                <div>{`${get(primaryAddress, "address1", "")}`}</div>
                                                <div>{`${get(get(primaryAddress, "city", {}), "city", "")}, ${get(
                                                    get(get(primaryAddress, "city", {}), "state", {}),
                                                    "stateCode",
                                                    ""
                                                )} ${get(get(primaryAddress, "zipCode", {}), "zipCode", "")}`}</div>
                                                <div>{propertyType}</div>
                                            </div>
                                        );
                                    case "Deal Type":
                                        return (
                                            <div
                                                key={`trc-${k}`}
                                                style={{
                                                    fontSize: "13px",
                                                    marginLeft: "4px",
                                                    flex: getTableCellFlex(header.name),
                                                    minWidth: getTableFixedWidth(header.name),
                                                    maxWidth: getTableFixedWidth(header.name),
                                                }}
                                            >
                                                <span>{transform(transactionType)}</span>
                                            </div>
                                        );
                                    case "Brokers":
                                        const brokerAgentsDisplay = brokerAgents.join(", ");
                                        return (
                                            <div
                                                key={`trc-${k}`}
                                                style={{
                                                    marginLeft: "2px",
                                                    flex: getTableCellFlex(header.name),
                                                    minWidth: getTableFixedWidth(header.name),
                                                    maxWidth: getTableFixedWidth(header.name),
                                                }}
                                            >
                                                <Tooltip
                                                    id={`brokers-tooltip-${k}`}
                                                    placement="top"
                                                    title={brokerAgentsDisplay}
                                                >
                                                    <StyledTooltipSpan
                                                        data-tooltip-id={`brokers-tooltip-${k}`}
                                                        data-tooltip-content={
                                                            brokerAgents.length > 1 ? brokerAgentsDisplay : ""
                                                        }
                                                    >
                                                        {header.transform(brokerAgents)}
                                                    </StyledTooltipSpan>
                                                </Tooltip>
                                            </div>
                                        );
                                    case "Batch Date":
                                        return (
                                            <div
                                                style={{
                                                    flex: getTableCellFlex(header.name),
                                                    minWidth: getTableFixedWidth(header.name),
                                                    maxWidth: getTableFixedWidth(header.name),
                                                }}
                                            >
                                                <span>{batchDate ? batchDate.toLocaleDateString() : "Unbatched"}</span>
                                            </div>
                                        );
                                    case "Documents":
                                        return (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    marginLeft: "-15px",
                                                    gap: "1vw",
                                                    flex: getTableCellFlex(header.name),
                                                }}
                                            >
                                                {sortedDocuments.map((document, document_index) => {
                                                    const isMissingDocument =
                                                        get(document, "mediaAssetKey", null) === null;

                                                    return role === "auditor" && isMissingDocument ? null : (
                                                        <Tooltip
                                                            id={`tr-${i}-doc-${document_index}`}
                                                            placement="top"
                                                            className="statBoxTooltip"
                                                            title={document?.documentTypeName}
                                                        >
                                                            <span
                                                                data-tooltip-id={`tr-${i}-doc-${document_index}`}
                                                                data-testid={`${listingName}-${document?.documentTypeName}`}
                                                            >
                                                                <StyledDocumentButton
                                                                    disabled={!document?.mediaAssetKey}
                                                                    onClick={() => {
                                                                        viewDocument(null, document?.mediaAssetKey);
                                                                    }}
                                                                    color="primary"
                                                                >
                                                                    <Doc
                                                                        fill={
                                                                            document?.mediaAssetKey != null
                                                                                ? "#2C99D2"
                                                                                : "#9E9E9E"
                                                                        }
                                                                    />
                                                                </StyledDocumentButton>
                                                            </span>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </div>
                                        );
                                    default:
                                        return (
                                            <div
                                                key={`trc-${k}`}
                                                style={{
                                                    flex: getTableCellFlex(header.name),
                                                    minWidth: getTableFixedWidth(header.name),
                                                    maxWidth: getTableFixedWidth(header.name),
                                                }}
                                            >
                                                {prop}
                                            </div>
                                        );
                                }
                            })}
                        </StyledTableRow>
                        <Collapse in={expanded} timeout="auto">
                            <div style={{ borderTop: "1px solid rgb(212, 212, 212)" }}>
                                {sortedDocuments.map((document, document_index) => (
                                    <div
                                        style={{
                                            fontSize: "13px",
                                            padding: `10px 10px 0px 0px`,
                                            borderBottom: "1px solid rgb(212, 212, 212)",
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <span data-tooltip-id={`tr-${i}-expandedDoc-${document_index}`}>
                                            <StyledDocumentButton
                                                disabled={get(document, "mediaAssetKey", null) === null}
                                                onClick={() => viewDocument(null, get(document, "mediaAssetKey"))}
                                                color="primary"
                                            >
                                                <Doc
                                                    fill={
                                                        get(document, "mediaAssetKey", null) !== null
                                                            ? "#2C99D2"
                                                            : "#9E9E9E"
                                                    }
                                                />
                                            </StyledDocumentButton>
                                        </span>
                                        <StyledDocumentButtonBase
                                            disabled={get(document, "mediaAssetKey", null) === null}
                                            onClick={() => viewDocument(null, get(document, "mediaAssetKey"))}
                                            color="primary"
                                        >
                                            <StyledDocumentLink document={document}>
                                                {get(document, "documentTypeName", "")}
                                            </StyledDocumentLink>
                                        </StyledDocumentButtonBase>
                                    </div>
                                ))}
                            </div>
                        </Collapse>
                    </div>
                );
            }}
        </Draggable>
    );
};

export default AuditListRow;
